import React, { useState, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FaUpload, FaImage, FaTimes } from 'react-icons/fa';

const FileUploadModal = ({ show, onHide, onSubmit, currentPath, loading }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (files) => {
    const imageFiles = Array.from(files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (imageFiles.length === 0) {
      alert('Please select only image files (JPEG, PNG, GIF, etc.)');
      return;
    }

    setSelectedFiles(prev => [...prev, ...imageFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      alert('Please select at least one image file');
      return;
    }
    onSubmit(selectedFiles);
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setDragActive(false);
    onHide();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Upload Images</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Current Path:</Form.Label>
            <Form.Control
              type="text"
              value={currentPath}
              readOnly
              style={{ backgroundColor: '#f8f9fa' }}
            />
          </Form.Group>

          <div
            className={`border-2 border-dashed rounded p-4 text-center ${
              dragActive ? 'border-primary bg-light' : 'border-secondary'
            }`}
            onDragEnter={handleDragIn}
            onDragLeave={handleDragOut}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            style={{ 
              borderStyle: 'dashed',
              cursor: 'pointer',
              minHeight: '120px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <FaUpload size={24} className="mb-2 text-muted" />
            <p className="mb-1">
              <strong>Click to select</strong> or drag and drop image files here
            </p>
            <p className="text-muted small mb-0">
              Only image files (JPEG, PNG, GIF, etc.) are allowed
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileSelect(e.target.files)}
              style={{ display: 'none' }}
            />
          </div>

          {selectedFiles.length > 0 && (
            <div className="mt-3">
              <h6>Selected Files ({selectedFiles.length}):</h6>
              <div className="max-h-40 overflow-y-auto">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="d-flex align-items-center p-2 border rounded mb-2">
                    <FaImage className="me-2 text-primary" />
                    <div className="flex-grow-1">
                      <div className="fw-bold">{file.name}</div>
                      <small className="text-muted">
                        {formatFileSize(file.size)} • {file.type}
                      </small>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeFile(index)}
                      disabled={loading}
                    >
                      <FaTimes />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={loading || selectedFiles.length === 0}
          >
            {loading ? 'Uploading...' : `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default FileUploadModal;
